/**
 * Карточка автопоста (бриф: сетка 3 в ряд): картинка 4:3 (у живого фото — «Фото: сайт»), тип, название,
 * три строки текста, внизу «Проверить: N», дата и кнопки «Открыть» / «Удалить». Клик по карточке ведёт в редактор.
 * Пост, который не вышел во ВК (failed), — с красной плашкой ошибки.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { CalendarClock, Check, Clock3, ExternalLink, Trash2, TriangleAlert } from "lucide-react";
import type { Post } from "@impuls/shared";
import { postExcerpt } from "@impuls/shared";
import { ApiError, api, errText } from "../lib/api";
import { ago, dateTime, humanDateTime } from "../lib/format";
import { useRefreshPosts } from "../lib/queries";
import { ECG_PATH } from "./Ecg";
import { PhotoCredit, SourceTag } from "./PostBits";
import { useConfirm, useToast } from "./ui";

/** Начало поста без заголовка капсом — он уже есть в названии карточки. */
function excerptOf(post: Post): string {
  const lines = post.text.trim().split("\n");
  const first = lines[0]?.trim() ?? "";
  const shouty = first.length > 0 && first.length < 140 && !/[a-zа-яё]/.test(first) && /[A-ZА-ЯЁ]/.test(first);
  const rest = shouty ? lines.slice(1).join("\n") : post.text;
  return postExcerpt(rest, "", 260);
}

function When({ post }: { post: Post }) {
  if (post.status === "scheduled" && post.scheduled_at) {
    const ms = post.scheduled_at * 1000;
    return (
      <span className="auto-card__when auto-card__when--scheduled" title={`Выйдет ${dateTime(ms)}`}>
        <CalendarClock size={15} aria-hidden="true" />
        <span className="sr-only">Выйдет</span>
        {humanDateTime(ms)}
      </span>
    );
  }
  if (post.status === "published" && post.published_at) {
    const ms = post.published_at * 1000;
    return (
      <span className="auto-card__when" title={`Опубликован ${dateTime(ms)}`}>
        <Check size={15} aria-hidden="true" />
        <span className="sr-only">Опубликован</span>
        {humanDateTime(ms)}
      </span>
    );
  }
  return (
    <span className="auto-card__when" title={`Подготовлен ${dateTime(post.created_at)}`}>
      <Clock3 size={15} aria-hidden="true" />
      <span className="sr-only">Подготовлен</span>
      {ago(post.created_at)}
    </span>
  );
}

export function AutoCard({ post }: { post: Post }) {
  const toast = useToast();
  const confirm = useConfirm();
  const refresh = useRefreshPosts();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const img = post.images[0];
  const checks = post.checks.length;
  const href = `/posts/${post.id}`;

  const remove = async () => {
    const ok = await confirm({
      title: "Удалить пост?",
      text:
        post.status === "scheduled"
          ? `«${post.title}» будет удалён, а отложенная запись во ВКонтакте — снята.`
          : post.status === "published"
            ? `«${post.title}» исчезнет из студии. Запись во ВКонтакте останется.`
            : `«${post.title}» исчезнет вместе с картинкой и источниками.`,
      confirm: "Удалить",
      danger: true,
    });
    if (!ok) return;
    // «Пост уже вышел во ВКонтакте» — не сбой: сервер отметил его опубликованным, карточка переедет в «Опубликованы»
    const fail = (e: unknown) => {
      const released = e instanceof ApiError && e.code === "vk_released";
      toast(errText(e), released ? "info" : "error");
      if (released) refresh(post.id);
    };
    setBusy(true);
    try {
      await api.del(`/api/posts/${post.id}`);
      toast("Пост удалён");
      refresh();
    } catch (e) {
      if (e instanceof ApiError && e.code === "vk_manual") {
        const force = await confirm({
          title: "Удалите запись во ВКонтакте вручную",
          text: e.message,
          confirm: "Я удалил во ВК — удалить пост",
          danger: true,
        });
        if (force) {
          try {
            await api.del(`/api/posts/${post.id}?force=1`);
            toast("Пост удалён");
            refresh();
          } catch (err) {
            fail(err);
          }
        }
      } else fail(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className={`auto-card ${post.status === "failed" ? "auto-card--failed" : ""} ${busy ? "is-busy" : ""}`}
      onClick={(e) => {
        // клик по пустому месту карточки — в редактор; ссылки и кнопки работают сами
        if ((e.target as HTMLElement).closest("a, button")) return;
        if (window.getSelection()?.toString()) return;
        navigate(href);
      }}
    >
      <div className="auto-card__media">
        {img ? (
          <>
            <img src={img.url} alt="" loading="lazy" decoding="async" />
            <PhotoCredit image={img} className="auto-card__credit" />
          </>
        ) : (
          <div className="auto-card__ph" aria-hidden="true">
            <svg viewBox="0 0 120 40" preserveAspectRatio="xMidYMid meet">
              <path d={ECG_PATH} />
            </svg>
          </div>
        )}
        <span className="auto-card__tag">
          <SourceTag source={post.source} international={post.international} />
        </span>
      </div>

      <div className="auto-card__body">
        <h3 className="auto-card__title">
          <Link to={href} className="auto-card__link">
            {post.title}
          </Link>
        </h3>
        <p className="auto-card__text">{excerptOf(post) || "Текста пока нет."}</p>

        <div className="auto-card__meta">
          {post.status !== "published" && (
            <span
              className={`chip ${checks > 0 ? "chip--warn" : "chip--outline"}`}
              title={checks > 0 ? post.checks.map((c) => `• ${c}`).join("\n") : undefined}
            >
              {checks > 0 ? `Проверить: ${checks}` : "Проверок нет"}
            </span>
          )}
          {post.status === "scheduled" && post.vk_dirty && <span className="chip chip--warn">Не обновлён во ВК</span>}
          {post.status === "failed" ? (
            <span className="chip chip--danger auto-card__error" title={post.error ?? undefined}>
              <TriangleAlert size={14} aria-hidden="true" />
              <span>{post.error || "Не вышел во ВКонтакте"}</span>
            </span>
          ) : (
            post.status !== "published" &&
            post.error && (
              <span className="chip chip--danger" title={post.error}>
                Ошибка публикации
              </span>
            )
          )}
          {post.status === "published" && post.vk_url && (
            <a className="auto-card__vk" href={post.vk_url} target="_blank" rel="noreferrer">
              <ExternalLink size={14} aria-hidden="true" />
              Во ВКонтакте
            </a>
          )}
          <When post={post} />
        </div>

        <div className="auto-card__actions">
          <Link className="btn btn--small" to={href}>
            Открыть
          </Link>
          <button className="btn btn--small btn--ghost btn--danger" onClick={() => void remove()} disabled={busy}>
            <Trash2 size={15} aria-hidden="true" />
            {busy ? "Удаляю…" : "Удалить"}
          </button>
        </div>
      </div>
    </article>
  );
}
